import { View } from "react-native-web";
import { useDatastore, usePersonaKey, useServerCallResult, useModuleMap, useModuleObject, useModuleGlobal } from "util/datastore"
import { DocumentLevelTooltip } from "open-modules/zdf/component/tooltip";
import { HorizBox, Pad } from "component/basics";
import { TextType, UtilityText } from "component/text";
import { Locked } from "@carbon/icons-react";
import { useHasCapability } from "component/admin";
import { TextButton } from "component/button";
import { useState } from "react";
import { StyleSheet } from "react-native";
import { PopupPanel } from "component/popup";
import { moduleMapInstanceToChannel, ChannelInstanceMappingEntry, moduleMapChannel, EditorialChannel, moduleUserGlobalChannel } from "open-modules/zdf/component/channels/channeltypes";
import { ChannelImage } from "./channel-management";
import { t, TLabel } from "util/stdtypes";


export type DropDownOption = {
    label: string | null;
    key: string;
    optionLeftWidget?: any;
    text?: string | null;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ChannelAssignmentDropDown({ instanceKey, structureKey }) {
    const datastore = useDatastore(); 
    const concatenatedKey = structureKey+"/"+instanceKey
    const currentAssignments = useModuleObject(moduleMapInstanceToChannel,concatenatedKey) || {}; //TODO: Figure out how to drop: || {}
    const currentAssignmentsArry:ChannelInstanceMappingEntry[] = Object.values(currentAssignments || {});

    //const [currentChannelKey, setCurrentChannelKey] = useState();
    //const [currentChannel, setCurrentChannel] = useState();
    //const [canAccessCurrentChannel, setCanAccessCurrentChannel] = useState(false);
    let canAccessCurrentChannel = true
    const allChannels = useModuleMap(moduleMapChannel);
    const allChannelsCount = Object.values(allChannels ?? {}).length;

    const userChannels = useMyChannels();
    const userChannelArray = Object.values(userChannels || {});
    let currentChannelKey = currentAssignmentsArry?.[0]?.channelKey || "";
    let currentChannel: EditorialChannel = {
        from: "",
        key: "",
        name: "",
        photoUrl: "",
        time: 0
    }; 
    if(currentChannelKey && allChannels){
        currentChannel = allChannels[currentChannelKey]
    }
    if (!currentChannelKey || currentChannelKey === "") {
        canAccessCurrentChannel = true;
    }
    else {
        const canAccess = userChannelArray?.find((userChannel) => userChannel.key === currentChannelKey);
        canAccessCurrentChannel = !!canAccess
    }
    // If there are no channels, don't show the dropdown menu.
    // If there is only one channel, do show it to allow reassigning questions/topics from no channel to that channel.
    if (allChannelsCount === 0) {
        // console.debug("No channels found, hiding dropdown")
        return;
    }
    let options:DropDownOption[] = userChannelArray.map((channel) => {
        return {
            text: channel.name,
            key: channel.key,
            optionLeftWidget: (
                <ChannelImage imageUrl={channel.photoUrl} width={24} height={24} />
            ),
            label: null,
        };
    });

    if (!currentChannelKey || currentChannelKey === "") {
        options = [{ label: "No channel", key: "no-channel" }, ...options];
    }

    if (options.length === 0) {
        options.push({ label: "Loading...", key: "loading" });
    }

    async function onSwitchChannelAsync(channelKey) {
        if (channelKey === "no-channel" || channelKey === "") {
            console.debug("Empty or no-channel key, hiding dropdown")
            return;
        }
        else {
            // If this question or topic was assigned to a channel before
            if (currentChannelKey && currentChannelKey !== "") {
                await datastore.callServerAsync("channel", "reassignContentToChannel", {
                    newChannelKey: channelKey,
                    instanceKey: instanceKey,
                    structureKey: structureKey,
                    oldChannelKey: currentChannelKey,
                });
            }
            // If this question or topic wasn't assigned to any channel before
            else {
                await datastore.callServerAsync("channel", "assignContentToChannel", {
                    instanceKey: instanceKey,
                    structureKey: structureKey,
                    channelKey: channelKey,
                });
            }
            currentChannelKey = channelKey;
        }currentChannel
    }
    return (
        <>
            {/* If this user does not have access to the currently assigned channel, don't show the dropdown, just show the channel as locked */}
            {canAccessCurrentChannel ? (
                <REPLACE_ZDF_DropDownSelector
                    options={options}
                    value={currentChannelKey}
                    onChange={(channelKey) => onSwitchChannelAsync(channelKey)}
                    testID={"channel-assignment-dropdown"}
                />
            ) : (
                <DocumentLevelTooltip label={t("You are not a member of this channel.")} width={175}>
                    <HorizBox center gap={8}>
                        <ChannelImage imageUrl={currentChannel?.photoUrl} width={24} height={24} />
                        <UtilityText text={currentChannel?.name} weight={"strong"} />
                        <Locked />
                    </HorizBox>
                </DocumentLevelTooltip>
            )}
        </>
    );
}

export function useMyChannels() {
    const canAccessAllChannels = useHasCapability("channel/access-all-channels");
    const personaKey = usePersonaKey();
    const allChannels = useModuleMap(moduleMapChannel);
    const defaultChannel = useDefaultChannel();
    const userMemberships = useServerCallResult("channel", "getUserChannels", { personaKey });

    if (canAccessAllChannels) {
        return Object.values(allChannels ?? {});
    } else {
        let userChannels = Object.values(allChannels ?? {}).filter((channel) => userMemberships?.includes(channel.key));
        if (defaultChannel) {
            userChannels = [...userChannels, defaultChannel];
        }
        return userChannels;
    }
}

// Displays the currently selected channel
export function useMyCurrentChannel() :EditorialChannel | null | undefined {
    const myChannels = useMyChannels();
    const currentChannel = useModuleGlobal(moduleUserGlobalChannel);
    return (
        (myChannels.filter((channel)=>channel.key === currentChannel)[0])??
        (myChannels.length > 0 ? myChannels[0] : null)
    );
}

export function useDefaultChannel() {
    const allChannelsMap = useModuleMap(moduleMapChannel) || {};
    const allChannelsArray = Object.values(allChannelsMap);
    const defaultChannel = allChannelsArray.find((channel) => channel.isDefault === true);
    return defaultChannel;
}

const ChannelDropDownSelectorStyle = StyleSheet.create({
    channelDropDownStyle: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ChannelSelectionDropDown() {
    const s = ChannelDropDownSelectorStyle;

    const datastore = useDatastore();
    const currentChannel = useMyCurrentChannel();
    const userChannels = useMyChannels();


    // Don't show the channel selection dropdown if there are no channels or only one channel
    const allChannels = useModuleMap(moduleMapChannel)
    const allChannelsCount = Object.values(allChannels ?? {}).length;
    if (allChannelsCount <= 1) {
        // console.debug("Hide channel, due to channel number <= 1")
        return;
    }

    function onSwitchChannel(channelKey) {
        datastore.setModuleGlobalAsync(moduleUserGlobalChannel,channelKey)
    }

    const options = userChannels.map((channel) => {
        return {
            text: channel.name,
            key: channel.key,
            optionLeftWidget: (
                <ChannelImage imageUrl={channel.photoUrl} width={24} height={24} />
            ),
            label: null,
        };
    }) || [];

    if (!(currentChannel?? options[0]?.key)) {
        return <UtilityText label={t("You are not a channel member, this disables certain functions!")} />;
    }

    return (
        options.length > 0 && (
            <>
                <View style={s.channelDropDownStyle}>
                    <UtilityText label={t("Channels")} weight="strong" />
                    <REPLACE_ZDF_DropDownSelector
                        options={options}
                        value={currentChannel?.key ?? options[0].key}
                        onChange={(channelKey) => onSwitchChannel(channelKey)}
                        testID={"Channels"} 
                    />
                </View>
            </>
        )
    );
}

/*
Changes:
- type is customizable, default is small instead of tiny
- supports displaying options as text or label
- supports options with an optionLeftWidget property that is rendered next to the option text/label
- slightly reduced space between options: 16px instead of 20px
*/

type DropDownSelectorProps = {
    testID: string,
    label?: TLabel,
    text?: string,
    type?: TextType,
    options: any[],
    value: string,
    onChange: (channelKey) => void
}
export function REPLACE_ZDF_DropDownSelector({ testID, label, text, type = "small", options, value, onChange = () => {} } : DropDownSelectorProps) {
    const [hover, setHover] = useState(false);
    const selectedOption = options.find((o) => o.key == value) || options[0];

    function popupContent({ onClose }) {
        return (
            <View>
                {options.map((o, i) => (
                    <View key={o.key + i}>
                        {i != 0 && <Pad size={16} />}
                        <HorizBox gap={8} center>
                            {o.optionLeftWidget && o.optionLeftWidget}
                            <TextButton
                                label={o.label}
                                text={o.text}
                                type={type as TextType}
                                onPress={() => {
                                    onChange(o.key);
                                    onClose();
                                }}
                            />
                        </HorizBox>
                    </View>
                ))}
            </View>
        );
    }

    return (
        <PopupPanel testID={testID} popupContent={popupContent} alignRight setHover={setHover}>
            <HorizBox center gap={8}>
                <UtilityText label={label} text={text} type={type} weight="strong" />
                {
                    (label || text) && (
                        <UtilityText text=": " type={type} weight="strong" />
                    ) /** Do not render a lone colon if no label provided*/
                }
                {selectedOption.optionLeftWidget && selectedOption.optionLeftWidget}
                <UtilityText
                    label={selectedOption.label}
                    text={selectedOption.text}
                    type={type}
                    underline={hover}
                    weight="strong"
                />
            </HorizBox>
        </PopupPanel>
    );
}
